// The words and rules of Settings, Activity log (spec C14, C15; the Settings board's Activity log tab),
// kept out of the page so they are tested on their own. Dates are New York days, YYYY-MM-DD.
import type { ActivityEntry, ActivityType, ActivityView } from '../auth/api';
import { roleLabels, type RoleCode } from '../roles';

export const activityCopy = {
  title: 'Activity log',
  sub: 'Every action in the portal, read only',
  date: 'Date',
  groupBy: 'Group by',
  filters: 'Filters',
  collapseAll: 'Collapse all',
  expandAll: 'Expand all',
  clearAll: 'Clear all',
  reset: 'Reset',
  apply: 'Apply',
  from: 'From',
  to: 'To',
  typing: 'Typing a date switches to Custom.',
  badDates: 'Enter dates like Nov 6, 2026.',
  backwards: 'From must be on or before To.',
  person: 'Person',
  searchPeople: 'Search people',
  noPeople: 'No people match',
  actionType: 'Action type',
  role: 'Role',
  empty: 'No activity matches these filters.',
  noRecord: 'No record (settings and sign-ins)',
  // proposed
  truncated: (n: number) =>
    `This range has more than ${n.toLocaleString('en-US')} actions; the newest are shown. Narrow the dates to see older ones.`,
  showing: (shown: number, all: number) => `Showing ${shown} of ${all}`,
  columns: ['When', 'Who', 'Record', 'Action', 'Before', 'After'],
} as const;

// ---------- dates ----------

const nyDay = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const nyTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const utc = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const dayOf = (date: Date) => date.toISOString().slice(0, 10);
export const addDays = (day: string, n: number) => {
  const d = utc(day);
  d.setUTCDate(d.getUTCDate() + n);
  return dayOf(d);
};

const short = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' });
const typed = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** "Oct 22" */
export const shortDay = (day: string) => short.format(utc(day));
/** "Oct 22, 2026": how the From and To fields show a date. */
export const typedDay = (day: string) => typed.format(utc(day));

const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** A typed date like "Nov 6, 2026" or "November 6 2026" as YYYY-MM-DD, or null. */
export function parseTyped(text: string): string | null {
  const m = /^\s*([a-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})\s*$/i.exec(text);
  if (!m) return null;
  const month = months.indexOf(m[1].slice(0, 3).toLowerCase());
  if (month < 0) return null;
  const day = `${m[3]}-${String(month + 1).padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return addDays(day, 0) === day ? day : null;
}

export type Range = { from: string; to: string };
export type PresetKey = 'today' | 'd7' | 'd30' | 'month' | 'last' | 'custom';

/** The board's presets, then Custom. */
export const presets: { key: PresetKey; label: string; range?: (today: string) => Range }[] = [
  { key: 'today', label: 'Today', range: (t) => ({ from: t, to: t }) },
  { key: 'd7', label: 'Last 7 days', range: (t) => ({ from: addDays(t, -6), to: t }) },
  { key: 'd30', label: 'Last 30 days', range: (t) => ({ from: addDays(t, -29), to: t }) },
  { key: 'month', label: 'This month', range: (t) => ({ from: `${t.slice(0, 8)}01`, to: t }) },
  {
    key: 'last',
    label: 'Last month',
    range: (t) => {
      const end = addDays(`${t.slice(0, 8)}01`, -1);
      return { from: `${end.slice(0, 8)}01`, to: end };
    },
  },
  { key: 'custom', label: 'Custom' },
];

export function presetOf(range: Range, today: string): PresetKey {
  const hit = presets.find((p) => {
    const r = p.range?.(today);
    return r && r.from === range.from && r.to === range.to;
  });
  return hit?.key ?? 'custom';
}

/** The date chip: a preset's name, or "Oct 1 to Oct 22". */
export function rangeLabel(range: Range, today: string): string {
  const key = presetOf(range, today);
  if (key !== 'custom') return presets.find((p) => p.key === key)!.label;
  return range.from === range.to
    ? shortDay(range.from)
    : `${shortDay(range.from)} to ${shortDay(range.to)}`;
}

/** The custom range typed in From and To, or the line under them that says what is wrong. */
export function checkTyped(from: string, to: string): Range | { error: string } {
  const f = parseTyped(from);
  const t = parseTyped(to);
  if (!f || !t) return { error: activityCopy.badDates };
  if (f > t) return { error: activityCopy.backwards };
  return { from: f, to: t };
}

/** The range in the address (?from=&to=), or null for the default. */
export function rangeFromSearch(params: URLSearchParams): Range | null {
  const from = params.get('from');
  const to = params.get('to');
  if (!from || !to) return null;
  const ok = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d) && addDays(d, 0) === d;
  return ok(from) && ok(to) && from <= to ? { from, to } : null;
}

/** When a row happened, in New York: the day and the time. */
export function when(iso: string) {
  const at = new Date(iso);
  const day = nyDay.format(at);
  return { day, date: shortDay(day), time: nyTime.format(at) };
}

/** A Day group's name: "Today, Oct 22", "Yesterday, Oct 21", "Tue, Oct 20". */
export function dayLabel(day: string, today: string): string {
  const first =
    day === today ? 'Today' : day === addDays(today, -1) ? 'Yesterday' : weekday.format(utc(day));
  return `${first}, ${shortDay(day)}`;
}

// ---------- filters ----------

/** Action types in the Filters popover, grouped as on the board; more join with renewals. */
export const typeGroups: { label: string; types: ActivityType[] }[] = [
  { label: 'Settings', types: ['Settings change'] },
  { label: 'Sign-ins', types: ['Sign-in'] },
];

/** The Role chips use the board's short names. */
export const roleChips: { code: RoleCode; label: string }[] = [
  { code: 'renewal_va', label: 'VA' },
  { code: 'account_executive', label: 'AE' },
  { code: 'manager', label: 'Manager' },
  { code: 'admin', label: 'Admin' },
];

export type Filters = { people: string[]; types: ActivityType[]; roles: RoleCode[] };
export const noFilters: Filters = { people: [], types: [], roles: [] };

export const passes = (e: ActivityEntry, f: Filters) =>
  (f.people.length === 0 || f.people.includes(e.who)) &&
  (f.types.length === 0 || f.types.includes(e.type)) &&
  (f.roles.length === 0 || (e.whoRole !== null && f.roles.includes(e.whoRole)));

/** The chips under the toolbar, one per filter in force. */
export function appliedLabels(f: Filters): { key: string; label: string }[] {
  return [
    ...f.people.map((p) => ({ key: `p:${p}`, label: `Person: ${p}` })),
    ...f.types.map((t) => ({ key: `t:${t}`, label: `Action: ${t}` })),
    ...f.roles.map((r) => ({
      key: `r:${r}`,
      label: `Role: ${roleChips.find((c) => c.code === r)!.label}`,
    })),
  ];
}

export function removeApplied(f: Filters, key: string): Filters {
  const [kind, value] = [key.slice(0, 1), key.slice(2)];
  if (kind === 'p') return { ...f, people: f.people.filter((p) => p !== value) };
  if (kind === 't') return { ...f, types: f.types.filter((t) => t !== value) };
  return { ...f, roles: f.roles.filter((r) => r !== value) };
}

export const toggle = <T>(list: T[], value: T) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/** Everyone in the portal, then anyone else in the log (Unknown email, Portal, Inflowmate). */
export function peopleOptions(view: ActivityView, search: string) {
  const roleOf = new Map(view.people.map((p) => [p.name, p.role]));
  const names = [...view.people.map((p) => p.name), ...view.entries.map((e) => e.who)].filter(
    (n, i, all) => all.indexOf(n) === i,
  );
  const q = search.trim().toLowerCase();
  return names
    .filter((n) => !q || n.toLowerCase().includes(q))
    .map((name) => {
      const role = roleOf.get(name);
      return {
        name,
        sub: role ? roleLabels[role] : 'System',
        count: view.entries.filter((e) => e.who === name).length,
      };
    });
}

// ---------- groups ----------

export type GroupBy = 'none' | 'day' | 'person' | 'type';

export const groupOptions: { key: GroupBy; label: string }[] = [
  { key: 'none', label: 'None' },
  { key: 'day', label: 'Day' },
  { key: 'person', label: 'Person' },
  { key: 'type', label: 'Action type' },
];

export type Group = {
  key: string;
  label: string;
  /** The person's role, when grouped by Person. */
  sub: string;
  rows: ActivityEntry[];
};

/** Rows in groups, newest group first (rows come newest first). */
export function groupRows(rows: ActivityEntry[], by: GroupBy, view: ActivityView, today: string) {
  const groups: Group[] = [];
  const roleOf = new Map(view.people.map((p) => [p.name, p.role]));
  for (const e of rows) {
    const key =
      by === 'day' ? when(e.at).day : by === 'person' ? e.who : by === 'type' ? e.type : 'all';
    let g = groups.find((x) => x.key === key);
    if (!g) {
      const role = roleOf.get(e.who);
      g = {
        key,
        label: by === 'day' ? dayLabel(key, today) : by === 'none' ? '' : key,
        sub: by === 'person' ? (role ? roleLabels[role] : 'System') : '',
        rows: [],
      };
      groups.push(g);
    }
    g.rows.push(e);
  }
  return groups;
}

export const countLabel = (n: number) => `${n} ${n === 1 ? 'action' : 'actions'}`;
export const latestLabel = (e: ActivityEntry) => {
  const w = when(e.at);
  return `Latest ${w.date} ${w.time}`;
};

/** Before and After longer than this show in full in a tooltip. */
export const longValue = 16;
